/**
 * SKOP Report Summary
 *
 * Displays summary of community reports for a token.
 */

import { useState, useEffect } from 'react';
import {
  getTokenReportSummary,
  generateMockReports,
  type TokenReportSummary as ReportSummaryType,
  type ReportReason,
  getReasonLabel,
  getReasonColor,
  formatReportTime,
} from '../lib/reports';
import { ReportModal } from './ReportModal';

interface ReportSummaryProps {
  tokenAddress: string;
  tokenSymbol: string;
  tokenName: string;
}

export function ReportSummary({ tokenAddress, tokenSymbol, tokenName }: ReportSummaryProps) {
  const [summary, setSummary] = useState<ReportSummaryType | null>(null);
  const [showReportModal, setShowReportModal] = useState(false);

  useEffect(() => {
    // Generate mock reports for demo
    generateMockReports(tokenAddress, tokenSymbol, tokenName);

    // Load summary
    const s = getTokenReportSummary(tokenAddress);
    setSummary(s);
  }, [tokenAddress, tokenSymbol, tokenName]);

  const handleReportSubmitted = () => {
    // Refresh summary
    const s = getTokenReportSummary(tokenAddress);
    setSummary(s);
  };

  if (!summary || summary.totalReports === 0) {
    return (
      <div className="card" style={{ marginBottom: '16px' }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ width: '14px', height: '14px', display: 'inline-flex', color: 'var(--text-secondary)' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
                <line x1="16" y1="13" x2="8" y2="13"/>
                <line x1="16" y1="17" x2="8" y2="17"/>
              </svg>
            </span>
            <span style={{ fontSize: '13px', fontWeight: 600 }}>Community Reports</span>
          </div>
          <button
            onClick={() => setShowReportModal(true)}
            style={{
              padding: '6px 12px',
              fontSize: '11px',
              fontWeight: 600,
              background: 'var(--surface-secondary)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-md)',
              color: 'var(--text-secondary)',
              cursor: 'pointer',
            }}
          >
            Report Token
          </button>
        </div>
        <div style={{
          marginTop: '12px',
          padding: '16px',
          background: 'var(--bg-tertiary)',
          borderRadius: 'var(--radius-md)',
          textAlign: 'center',
          color: 'var(--text-tertiary)',
          fontSize: '13px',
        }}>
          No community reports for this token
        </div>

        {showReportModal && (
          <ReportModal
            tokenAddress={tokenAddress}
            tokenSymbol={tokenSymbol}
            tokenName={tokenName}
            onClose={() => setShowReportModal(false)}
            onSubmit={handleReportSubmitted}
          />
        )}
      </div>
    );
  }

  const hasVerified = summary.verifiedReports > 0;
  const reasons = Object.entries(summary.reasons)
    .filter(([, count]) => count > 0)
    .sort(([, a], [, b]) => b - a) as [ReportReason, number][];

  return (
    <div className="card" style={{
      marginBottom: '16px',
      border: hasVerified ? '1px solid rgba(255, 59, 59, 0.3)' : undefined,
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '12px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ width: '14px', height: '14px', display: 'inline-flex', color: hasVerified ? '#ff3b3b' : '#ffb800' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
              <line x1="12" y1="9" x2="12" y2="13"/>
              <line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
          </span>
          <span style={{
            fontSize: '13px',
            fontWeight: 600,
            color: hasVerified ? '#ff3b3b' : '#ffb800',
          }}>
            Community Reports ({summary.totalReports})
          </span>
          {hasVerified && (
            <span style={{
              padding: '2px 6px',
              fontSize: '10px',
              fontWeight: 600,
              background: 'rgba(255, 59, 59, 0.15)',
              color: '#ff3b3b',
              borderRadius: 'var(--radius-sm)',
            }}>
              {summary.verifiedReports} verified
            </span>
          )}
        </div>
        <button
          onClick={() => setShowReportModal(true)}
          style={{
            padding: '6px 12px',
            fontSize: '11px',
            fontWeight: 600,
            background: 'rgba(255, 184, 0, 0.1)',
            border: '1px solid rgba(255, 184, 0, 0.3)',
            borderRadius: 'var(--radius-md)',
            color: '#ffb800',
            cursor: 'pointer',
          }}
        >
          Report Token
        </button>
      </div>

      {/* Reason Breakdown */}
      <div style={{
        padding: '12px',
        background: 'var(--bg-tertiary)',
        borderRadius: 'var(--radius-md)',
      }}>
        {reasons.map(([reason, count]) => (
          <div
            key={reason}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '6px 0',
              borderBottom: reasons.indexOf([reason, count] as [ReportReason, number]) < reasons.length - 1
                ? '1px solid var(--border)'
                : undefined,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: getReasonColor(reason),
              }} />
              <span style={{ fontSize: '13px' }}>{getReasonLabel(reason)}</span>
            </div>
            <span style={{
              fontSize: '13px',
              fontWeight: 600,
              color: 'var(--text-secondary)',
            }}>
              {count} report{count > 1 ? 's' : ''}
            </span>
          </div>
        ))}
      </div>

      {/* Last Report */}
      <div style={{
        marginTop: '12px',
        fontSize: '11px',
        color: 'var(--text-tertiary)',
        textAlign: 'right',
      }}>
        Latest report: {formatReportTime(summary.latestReportAt)}
      </div>

      {showReportModal && (
        <ReportModal
          tokenAddress={tokenAddress}
          tokenSymbol={tokenSymbol}
          tokenName={tokenName}
          onClose={() => setShowReportModal(false)}
          onSubmit={handleReportSubmitted}
        />
      )}
    </div>
  );
}
